import {z} from 'zod';

import {IsoUtcDateTimeSchema, UuidV4Schema} from './common.js';

/**
 * Contratos da porta interna de publicação.
 *
 * Mesma família de `internal-editorial.ts`: quem chama é outro serviço da
 * própria operação, a identidade vem do certificado da conexão e não existe
 * bearer neste caminho.
 *
 * Duas decisões carregam este arquivo:
 *
 * 1. **`asOf` é CORRELAÇÃO, e nunca o relógio do ciclo.** O plano 03 diz isso
 *    com todas as letras: validade, janela e limite usam o relógio do servidor.
 *    O campo existe para casar a execução do agendador com o que a API fez, e
 *    aceitar que ele mande o tempo para trás seria deixar o chamador reabrir
 *    uma janela que já fechou.
 * 2. **`accountId` é conferência, e não escolha.** O processo resolve a conta
 *    uma vez, no arranque, e esta porta só confirma que o chamador está falando
 *    da mesma. Deixar a requisição escolher a conta é o caminho para publicar
 *    na conta errada, e post publicado não se desfaz.
 */

/** Estado com que uma execução interna nasce. Ela roda depois da resposta. */
export const InternalRunStatusSchema = z.enum(['queued']);
export type InternalRunStatus = z.infer<typeof InternalRunStatusSchema>;

export const PublishingDispatchRequestSchema = z.strictObject({
  /** `instagram_accounts.id` do ambiente deste processo. */
  accountId: UuidV4Schema,
  /** Instante de correlação do agendador. Nunca vira o relógio do ciclo. */
  asOf: IsoUtcDateTimeSchema,
});
export type PublishingDispatchRequest = z.infer<
  typeof PublishingDispatchRequestSchema
>;

export const PublishingDispatchResponseSchema = z.strictObject({
  runId: UuidV4Schema,
  status: InternalRunStatusSchema,
});
export type PublishingDispatchResponse = z.infer<
  typeof PublishingDispatchResponseSchema
>;

/** Reconciliação usa exatamente o mesmo corpo do despacho. */
export const PublishingReconcileRequestSchema = PublishingDispatchRequestSchema;
export type PublishingReconcileRequest = z.infer<
  typeof PublishingReconcileRequestSchema
>;

export const TokenHealthRequestSchema = z.strictObject({
  accountId: UuidV4Schema,
});
export type TokenHealthRequest = z.infer<typeof TokenHealthRequestSchema>;

export const TokenHealthResponseSchema = z.strictObject({
  checkId: UuidV4Schema,
  status: InternalRunStatusSchema,
});
export type TokenHealthResponse = z.infer<typeof TokenHealthResponseSchema>;
